import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Container, 
  Paper, 
  TextField, 
  Typography, 
  Stepper, 
  Step, 
  StepLabel,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  Autocomplete
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useFirebase } from './contexts/FirebaseContext';
import { useAuth } from './contexts/AuthContext';
import { useError } from './contexts/ErrorContext';
import { signInWithCustomToken, signInWithPhoneNumber, RecaptchaVerifier } from 'firebase/auth';
import { checkExistingPhoneUser, createOrUpdateUserDocument } from './utilities/authUtils';
import { useApi } from './services/api';

function SignInWhatsApp() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+44'); // Default to UK
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0); // 0: Phone input, 1: Code verification
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [countryCodeError, setCountryCodeError] = useState(null);
  
  // Dialog state for existing account
  const [openDialog, setOpenDialog] = useState(false);
  
  // Track verification method (whatsapp or firebase-sms)
  const [verificationMethod, setVerificationMethod] = useState('whatsapp');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const recaptchaVerifierRef = useRef(null);
  
  // Country codes data
  const countryCodes = [
    { code: '+44', country: 'UK', flag: '🇬🇧' },
    { code: '+1', country: 'US/CA', flag: '🇺🇸' },
    { code: '+61', country: 'AU', flag: '🇦🇺' },
    { code: '+64', country: 'NZ', flag: '🇳🇿' },
    { code: '+27', country: 'ZA', flag: '🇿🇦' },
    { code: '+353', country: 'IE', flag: '🇮🇪' },
    { code: '+33', country: 'FR', flag: '🇫🇷' },
    { code: '+49', country: 'DE', flag: '🇩🇪' },
    { code: '+34', country: 'ES', flag: '🇪🇸' },
    { code: '+39', country: 'IT', flag: '🇮🇹' },
  ];
  
  const { currentUser } = useAuth();
  const { auth, db } = useFirebase();
  const navigate = useNavigate();
  const { post } = useApi();
  const { logAuthenticationError, logValidationError } = useError();

  // Validate country code format
  const validateCountryCode = (code) => {
    // Must start with + and be followed by 1-4 digits
    const countryCodeRegex = /^\+[1-9]\d{0,3}$/;
    return countryCodeRegex.test(code);
  };
  
  // Helper function to get or create reCAPTCHA verifier
  const getOrCreateRecaptchaVerifier = () => {
    if (recaptchaVerifierRef.current) {
      return recaptchaVerifierRef.current;
    }
    
    // Create a div for the recaptcha if it doesn't exist
    if (!document.getElementById('recaptcha-container-phone')) {
      const recaptchaContainer = document.createElement('div');
      recaptchaContainer.id = 'recaptcha-container-phone';
      document.body.appendChild(recaptchaContainer);
    }
    
    try {
      recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'recaptcha-container-phone', {
        size: 'invisible',
        callback: () => {
          console.log('reCAPTCHA solved');
        }
      });
      console.log('reCAPTCHA verifier created');
      return recaptchaVerifierRef.current;
    } catch (error) {
      console.error('Error creating reCAPTCHA verifier:', error);
      throw error;
    }
  };

  useEffect(() => {
    // Clean up on component unmount
    return () => {
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
          recaptchaVerifierRef.current = null;
          console.log('reCAPTCHA cleared successfully');
        } catch (error) {
          console.error('Error clearing recaptcha ref:', error);
        }
      }
      
      // Remove the container div
      const container = document.getElementById('recaptcha-container-phone');
      if (container) {
        container.remove();
      }
    };
  }, []);

  const handlePhoneSubmit = async () => {
    if (!phoneNumber) {
      const errorMessage = 'Please enter a phone number';
      setError(errorMessage);
      
      // Log validation error
      await logValidationError(new Error(errorMessage), 'phone_number', {
        step: 'phone_input',
        country_code: countryCode
      });
      return;
    }

    if (!validateCountryCode(countryCode)) {
      const errorMessage = 'Please enter a valid country code (e.g., +44, +1, +33)';
      setCountryCodeError(errorMessage);
      
      // Log validation error
      await logValidationError(new Error(errorMessage), 'country_code', {
        step: 'phone_input',
        country_code: countryCode,
        phone_number: phoneNumber
      });
      return;
    }
    
    setLoading(true);
    setError(null);
    setCountryCodeError(null);
    
    const cleanedPhoneNumber = phoneNumber.replace(/[^0-9]/g, '');
    
    const formattedPhoneNumber = `${countryCode}${cleanedPhoneNumber}`;

    try {      
      // Check if the phone number is already registered
      const { exists, uid } = await checkExistingPhoneUser(db, formattedPhoneNumber);
      
      if (exists && currentUser && uid !== currentUser.uid) {
        // Phone number belongs to a different account
        setOpenDialog(true);
        setLoading(false);
        return;
      }
      
      // If the user is already signed in and trying to link this phone number
      if (currentUser) {
        // This is a phone number linking flow
        console.log('Linking phone number to existing account');
      }

      const data = await post('/auth/whatsapp/send-otp', {
        phoneNumber: formattedPhoneNumber
      });
      
      console.log('Backend response:', { ok: true, status: 200, data });
      
      // Check if we need to fall back to Firebase SMS
      if (data.method === 'firebase' || (data.success && data.method === 'firebase')) {
        setVerificationMethod('firebase');
        setSuccess('WhatsApp unavailable, using SMS verification instead...');
        
        // Initialize Firebase phone auth using centralized function
        const recaptchaVerifier = getOrCreateRecaptchaVerifier();
        
        // Send SMS via Firebase
        const confirmationResult = await signInWithPhoneNumber(auth, formattedPhoneNumber, recaptchaVerifier);
        setConfirmationResult(confirmationResult);
        setActiveStep(1); // Move to verification step
        setSuccess('SMS verification code sent successfully');
      } else if (data.success && (data.method === 'whatsapp' || !data.method)) {
        setVerificationMethod('whatsapp');
        setActiveStep(1); // Move to verification step
        setSuccess('WhatsApp verification code sent successfully');
      } else if (!data.success) {
        // Handle failed responses - check if it's a fallback scenario
        if (data.error && (data.error.includes('Falling back to SMS') || data.error.includes('configuration error'))) {
          setVerificationMethod('firebase');
          setSuccess('WhatsApp unavailable, using SMS verification instead...');
          
          const recaptchaVerifier = getOrCreateRecaptchaVerifier();
          
          const confirmationResult = await signInWithPhoneNumber(auth, formattedPhoneNumber, recaptchaVerifier);
          setConfirmationResult(confirmationResult);
          setActiveStep(1);
          setSuccess('SMS verification code sent successfully');
        } else {
          throw new Error(data.error || 'Failed to send verification code');
        }
      } else {
        throw new Error(data.error || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Error sending verification code:', error);
      const errorMessage = error.message || 'Failed to send verification code';
      setError(errorMessage);
      
      // Log user-facing authentication error
      await logAuthenticationError(error, 'whatsapp_send_verification');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode) {
      const errorMessage = 'Please enter the verification code';
      setError(errorMessage);
      
      // Log validation error
      await logValidationError(new Error(errorMessage), 'verification_code', {
        step: 'code_verification',
        verification_method: verificationMethod
      });
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const cleanedPhoneNumber = phoneNumber.replace(/[^0-9]/g, '');
      const formattedPhoneNumber = `${countryCode}${cleanedPhoneNumber}`;
      
      let data;
      
      if (verificationMethod === 'firebase' && confirmationResult) {
        // Verify using Firebase phone auth
        const userCredential = await confirmationResult.confirm(verificationCode);
        const user = userCredential.user;
        
        // Get the ID token to send to backend
        const idToken = await user.getIdToken();
        
        // Verify with backend to create custom token
        data = await post('/auth/whatsapp/verify-otp', {
          phoneNumber: formattedPhoneNumber,
          otp: verificationCode, // Still send OTP for logging purposes
          method: 'firebase',
          firebaseIdToken: idToken
        });
      } else {
        // Verify using WhatsApp/Twilio
        data = await post('/auth/whatsapp/verify-otp', {
          phoneNumber: formattedPhoneNumber,
          otp: verificationCode,
          method: 'whatsapp'
        });
      }

      if (data.customToken) {
        const userCredential = await signInWithCustomToken(auth, data.customToken);
        const user = userCredential.user;

        await createOrUpdateUserDocument(user, db, {
          phoneNumber: formattedPhoneNumber,
          phoneVerified: true,
          registrationMethod: currentUser ? 'linked_phone' : 'phone'
        });
        
        navigate('/dashboard', { 
          state: { 
            message: currentUser 
              ? 'Phone number linked successfully!' 
              : 'Phone authentication successful!'
          } 
        });
      } else {
        throw new Error(data.error || 'Invalid verification code');
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      const errorMessage = error.message || 'Failed to verify code';
      setError(errorMessage);
      
      // Log user-facing authentication error
      await logAuthenticationError(error, 'whatsapp_verify_code');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setActiveStep(0);
    setVerificationCode('');
    setError(null);
    setSuccess(null);
  };
  
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  
  const handleNavigateToLogin = () => {
    setOpenDialog(false);
    navigate('/login');
  };

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          {currentUser ? 'Link Phone Number' : 'Phone Authentication'}
        </Typography>
        
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          <Step>
            <StepLabel>Enter Phone</StepLabel>
          </Step>
          <Step>
            <StepLabel>Verify Code</StepLabel>
          </Step>
        </Stepper>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        {activeStep === 0 ? (
          <Box>
            <Typography variant="body1" paragraph>
              {currentUser 
                ? 'Link your phone number to your account for additional security and easier sign-in.' 
                : 'Enter your phone number to receive a verification code via WhatsApp.'}
            </Typography>
            
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  freeSolo
                  options={countryCodes.map((country) => country.code)}
                  value={countryCode}
                  onChange={(event, newValue) => {
                    setCountryCode(newValue || '+44');
                    setCountryCodeError(null);
                  }}
                  onInputChange={(event, newInputValue) => {
                    setCountryCode(newInputValue || '+44');
                    setCountryCodeError(null);
                  }}
                  renderOption={(props, option) => {
                    const country = countryCodes.find(c => c.code === option);
                    return (
                      <Box 
                        component="li" 
                        {...props}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          py: { xs: 1.5, sm: 1 },
                          px: { xs: 2, sm: 1.5 },
                          minHeight: { xs: '48px', sm: '40px' },
                          fontSize: { xs: '0.9rem', sm: '0.875rem' },
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Box component="span" sx={{ fontSize: '1.2em', minWidth: '24px' }}>
                          {country.flag}
                        </Box>
                        <Box component="span" sx={{ fontWeight: 600, minWidth: 'fit-content' }}>
                          {country.code}
                        </Box>
                        <Box component="span" sx={{ 
                          color: 'text.secondary', 
                          fontSize: '0.85em',
                          display: { xs: 'none', sm: 'inline' }
                        }}>
                          ({country.country})
                        </Box>
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country Code"
                      error={!!countryCodeError}
                      helperText={countryCodeError}
                      disabled={loading}
                      placeholder="+44"
                      sx={{
                        '& .MuiInputBase-input': {
                          minWidth: { xs: '70px', sm: '60px' },
                        },
                      }}
                    />
                  )}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  required
                  fullWidth
                  id="phoneNumber"
                  label="Phone Number"
                  name="phoneNumber"
                  autoComplete="tel"
                  value={phoneNumber}
                  onChange={(e) => {
                    let input = e.target.value;

                    // Remove all non-numeric characters
                    input = input.replace(/[^\d]/g, '');

                    // If starts with 0, remove it (common in UK numbers)
                    if (input.startsWith('0')) {
                      input = input.slice(1);
                    }

                    setPhoneNumber(input);
                  }}
                  placeholder={countryCode === '+44' ? '7123456789' : '1234567890'}
                  disabled={loading}
                  autoFocus
                />
                <Typography variant="body2" sx={{ mt: 1 }}>To receive Wagl notifications and game invitations via WhatApp, you must provide a mobile phone number.</Typography>
              </Grid>
            </Grid>
            
            <Button
              fullWidth
              variant="contained"
              onClick={handlePhoneSubmit}
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Send Verification Code'}
            </Button>
            
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIcon />}
              sx={{ mt: 1 }}
              disabled={loading}
            >
              Back
            </Button>
          </Box>
        ) : (
          <Box>
            <Typography variant="body1" paragraph>
              Enter the verification code sent to your {verificationMethod === 'firebase' ? 'phone via SMS' : 'WhatsApp'} at {countryCode}{phoneNumber}.
            </Typography>
            
            {verificationMethod === 'firebase' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                WhatsApp verification is temporarily unavailable. We've sent you an SMS instead.
              </Alert>
            )}
            
            <TextField
              margin="normal"
              required
              fullWidth
              id="verificationCode"
              label="Verification Code"
              name="verificationCode"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              disabled={loading}
              autoFocus
            />
            
            <Button
              fullWidth
              variant="contained"
              onClick={handleVerifyCode}
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Verify Code'}
            </Button>
            
            <Button
              fullWidth
              variant="outlined"
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
              sx={{ mt: 1 }}
              disabled={loading}
            >
              Back
            </Button>
          </Box>
        )}
        
        <div id="recaptcha-container-phone" style={{ margin: '10px 0' }}></div>
      </Paper>
      
      {/* Dialog for existing account */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Phone Number Already Registered</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This phone number is already linked to an existing account. 
            {currentUser ? 
              ' You cannot link a phone number that is already associated with another account.' : 
              ' Please sign in with your existing account or use a different phone number.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          {!currentUser && (
            <Button onClick={handleNavigateToLogin} variant="contained">
              Go to Sign In
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default SignInWhatsApp;
